// The fan language switch (#1117): a native select, so every template gets
// the platform's own accessible picker on phones and desktops. Templates
// style it through class names or styles; it renders nothing unless the
// league offers two or more languages.

import type { CSSProperties, ReactNode } from 'react'
import { sf } from '@/site/core/features/sf'
import type { Locale } from '@/i18n'

/** The shape of core's useLanguageSwitch() (useSiteFooter().language). */
export interface LanguageSelectModel {
  visible: boolean
  label: string
  value: Locale
  options: Array<{ value: Locale; label: string }>
  set: (locale: Locale) => void
}

/** A touch screen (coarse pointer): tap targets must be at least 44px. */
export function isCoarsePointer(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
}

// `overlay`: the select is stretched, transparent, over the whole control, so
// a tap anywhere on it opens the picker; the control shows the picked name.
const OVERLAY: CSSProperties = { position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', fontSize: 16 }

export function LanguageSelect({ model, className, style, labelClassName, selectClassName, selectStyle, before, after, overlay }: {
  model: LanguageSelectModel
  className?: string
  style?: CSSProperties
  /** The "Language" label. */
  labelClassName?: string
  selectClassName?: string
  selectStyle?: CSSProperties
  /** Decoration around the select (an icon, a chevron); keep it aria-hidden. */
  before?: ReactNode
  after?: ReactNode
  /** Show the picked language as text and lay the select over the whole control. */
  overlay?: boolean
}) {
  if (!model.visible) return null
  const picked = model.options.find((o) => o.value === model.value)?.label ?? model.value
  return (
    <label className={className} style={overlay ? { position: 'relative', ...style } : style} {...sf('site.footer.language')}>
      {before}
      <span className={labelClassName}>{model.label}</span>
      {overlay && <span aria-hidden="true">{picked}</span>}
      <select
        className={selectClassName}
        style={overlay ? { ...OVERLAY, ...selectStyle } : selectStyle}
        value={model.value}
        onChange={(e) => model.set(e.target.value as Locale)}
      >
        {model.options.map((o) => (
          <option key={o.value} value={o.value} lang={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {after}
    </label>
  )
}
